import * as React from "react";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "./icons";
import { Select as SelectPrimitive } from "radix-ui";

import { cn } from "./utils";
import { Elevated } from "./elevated";

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  /** Mirrors Input's ladder — sm h-6 · default h-7 · `field` h-8, the 32px box
   *  a control takes as the VALUE of a labeled field in a reading column. See
   *  the doc on `Input`'s `size` for why that tier is role-named. */
  size?: "sm" | "default" | "field";
  /**
   * `default` — the form-field look: square-ish `rounded-md` box on the page
   * background. Correct in a form, where it reads as the same family as Input.
   *
   * `pill` — a soft-filled, fully-rounded chip for a select that is a MENU
   * CONTROL, not a form field (the display panel's Grouping / Ordering rows).
   *
   * It keeps a VISIBLE hairline. The first cut dropped the border on the theory
   * that the fill alone reads as tappable — but a 7% fill over a `rgb(37,37,37)`
   * panel lifts it by ~15 levels, which is no edge at all on a real display
   * (Matt: "you can't see them"). Both the border and the fill are foreground
   * alphas so the chip holds its contrast in either theme.
   */
  variant?: "default" | "pill";
}) {
  const pill = variant === "pill";
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-variant={variant}
      className={cn(
        // Harmonized with the Input atom so dropdowns read as the same
        // form control family: same border token, bg, hover/focus border,
        // and no shadow. Keeps the chevron + size variants + a11y states.
        // leading-none + text-box cap-trim = the uniform control-centering
        // model (mirrors Button/Input, PRD D4).
        //
        // …but the VALUE slot gets the full `type-body` pair back. That is a bug
        // fix, not a style tweak. `leading-none` makes the line box exactly the font
        // size (13px at type-body) while `line-clamp-1` leaves `overflow:hidden`
        // on that same box — so every descender rendered BELOW the baseline was
        // sliced off: "In progress" lost the tail of its g, and so did every
        // "High"/"Assigned"/"Copy"/"Deploying" value in the app. Cap-trim centres
        // the trigger against its CAP height by design, which is exactly why the
        // box it leaves behind cannot also be the box that clips.
        //
        // Safe against the h-7 contract: the trigger is a fixed-height
        // `items-center` flex, so the 20px inner line box centres inside the
        // 28px control instead of growing it. Heights are unchanged — verified.
        "border-foreground/15 data-[placeholder]:text-foreground [&_svg:not([class*='text-'])]:text-foreground aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex w-fit items-center justify-between gap-2 rounded-md border bg-background px-3 py-1 type-body leading-none [text-box:trim-both_cap_alphabetic] whitespace-nowrap transition-[color,box-shadow] outline-none hover:border-foreground/25 focus-visible:border-foreground/40 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-7 data-[size=sm]:h-6 data-[size=field]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 *:data-[slot=select-value]:type-body [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        pill &&
          "rounded-full border-foreground/25 bg-foreground/[0.08] pl-3 pr-2 transition-colors hover:border-foreground/40 hover:bg-foreground/[0.14] focus-visible:border-foreground/40 focus-visible:bg-foreground/[0.14]",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon
          className={cn(pill ? "size-3.5 opacity-40" : "size-4 opacity-50")}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "center",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <Elevated offset={2} shadowLevel={3} asChild>
        <SelectPrimitive.Content
          data-slot="select-content"
          className={cn(
            // z-[90]: floating menu layer — above Dialog/Sheet (z-[80]) so Model
            // pickers inside Add-agent (and other Stage modals) aren't clipped under
            // the dialog. See packages/ui/src/dialog.tsx z-ledger.
            "text-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[90] max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md",
            position === "popper" &&
              "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className,
          )}
          position={position}
          align={align}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </Elevated>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("text-foreground px-2 py-1.5 type-body", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        // Disabled options signal by COLOUR, not `opacity-50`: opacity is a
        // stacking context that dims the whole subtree, and a locked option's
        // reason (rendered as a child span in the editable peek) could not
        // escape it — the one sentence the lock exists to be read landed at
        // 3.96:1, below AA. `text-muted-foreground` at full opacity measures AA
        // in both themes (5.65:1 light / 5.21:1 dark on the popover) and still
        // reads as unavailable alongside `pointer-events-none`. (#43818)
        "focus:bg-accent focus:text-foreground [&_svg:not([class*='text-'])]:text-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 type-body outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute right-2 flex size-3.5 items-center justify-center"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(
        "bg-border pointer-events-none -mx-1 my-1 h-px",
        className,
      )}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
